import { useState, useEffect } from "react";
import type { Database } from "@/integrations/supabase/types";

type SupportedLanguage = Database["public"]["Enums"]["supported_language"];
type ParticipantGroup = Database["public"]["Enums"]["participant_group"];
type Survey = Database["public"]["Tables"]["surveys"]["Row"];

interface UseSurveyFormProps {
  survey: Survey;
  language: SupportedLanguage;
  participantGroup?: ParticipantGroup | null;
  previousConsent?: {
    studyConsent: boolean;
    dataConsent: boolean;
    phoneNumber: string;
  } | null;
  previousAnswers?: Record<string, string> | null;
  currentStep: number;
  onSubmit: (answers: Record<string, string>, language: SupportedLanguage, phoneNumber?: string, responseTime?: number, studyConsent?: boolean, dataConsent?: boolean) => Promise<void>;
}

export const useSurveyForm = ({
  survey,
  language,
  participantGroup,
  previousConsent,
  previousAnswers,
  currentStep,
  onSubmit
}: UseSurveyFormProps) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [startTime, setStartTime] = useState(Date.now());
  const [hasStudyConsent, setHasStudyConsent] = useState(false);
  const [hasDataConsent, setHasDataConsent] = useState(false);
  // Track both answers text and index position to help with language switching
  const [answerIndices, setAnswerIndices] = useState<Record<string, number>>({});
  // Track checkbox selections separately 
  const [checkboxSelections, setCheckboxSelections] = useState<Record<string, Record<string, boolean>>>({});

  // Reset state when survey changes (defensive against stale state during transitions)
  useEffect(() => {
    setAnswers({});
    setIsSubmitting(false);
    setAnswerIndices({});
    setCheckboxSelections({});
    setStartTime(Date.now());
  }, [survey.id]);

  // Use previous consent data if available or automatically show questions for follow-up surveys
  useEffect(() => {
    console.log('useSurveyForm effect - currentStep:', currentStep, 'previousConsent:', previousConsent);
    
    // CRITICAL: For follow-up surveys (currentStep > 0), ALWAYS skip consent form
    if (currentStep > 0) {
      console.log('Follow-up survey detected, skipping consent form');
      setShowQuestions(true);
      
      // For follow-up surveys, consent must exist from first response
      // Set to true by default, will be overwritten when previousConsent loads
      setHasStudyConsent(previousConsent?.studyConsent ?? true);
      setHasDataConsent(previousConsent?.dataConsent ?? true);
      setPhoneNumber(previousConsent?.phoneNumber || "");
      
      if (previousConsent) {
        console.log('Loaded previous consent:', previousConsent);
      } else {
        console.log('Using default consent values (true) for follow-up survey');
      }
    } else if (previousConsent) {
      // First survey (currentStep === 0) with previous consent
      setHasStudyConsent(previousConsent.studyConsent);
      setHasDataConsent(previousConsent.dataConsent);
      setPhoneNumber(previousConsent.phoneNumber || "");
      
      // If both consents are already given, we can show questions directly
      if (previousConsent.studyConsent && previousConsent.dataConsent) {
        setShowQuestions(true);
      }
    }
    
    // Load previous answers if available
    if (previousAnswers) {
      setAnswers(previousAnswers);
      console.log('Loaded previous answers into form:', previousAnswers);
    }
  }, [previousConsent, previousAnswers, currentStep]);

  // Reset start time when questions appear, but not when language changes
  useEffect(() => {
    if (showQuestions) {
      setStartTime(Date.now());
    }
  }, [showQuestions]);

  const mapAnswersToNewLanguage = (newLanguage: SupportedLanguage) => {
    if (!survey.questions || typeof survey.questions !== 'object') {
      console.log('No questions available for mapping');
      return answers;
    }

    try {
      // Get current and new language questions
      const currentQuestions = (survey.questions[language] || []) as Array<{id: string, type: string, options?: string[]}>; 
      const newQuestions = (survey.questions[newLanguage] || []) as Array<{id: string, type: string, options?: string[]}>; 
      
      if (!currentQuestions.length || !newQuestions.length) {
        console.log('Missing questions for language mapping');
        return answers;
      }
      
      // Create a new answers object that maps the old answers to the new question IDs
      const updatedAnswers: Record<string, string> = {};
      
      // Process each question that has an answer
      for (const [questionId, answer] of Object.entries(answers)) {
        // Find the current question
        const currentQuestion = currentQuestions.find(q => q.id === questionId);
        if (!currentQuestion) continue;
        
        // Find the corresponding question in the new language (same index)
        const currentIndex = currentQuestions.findIndex(q => q.id === questionId);
        if (currentIndex === -1) continue;
        
        const newQuestion = newQuestions[currentIndex];
        if (!newQuestion) continue;
        
        // Handle different question types
        if (currentQuestion.type === 'checkbox' && 
            currentQuestion.options && 
            newQuestion.options &&
            answer) {
          
          // For checkbox questions, we need to map each selected option
          const selectedOptions = answer.split(',');
          const mappedOptions = selectedOptions.map(selectedOption => {
            const optionIndex = currentQuestion.options?.findIndex(opt => opt === selectedOption) || -1;
            if (optionIndex !== -1 && optionIndex < (newQuestion.options?.length || 0)) {
              return newQuestion.options?.[optionIndex] || selectedOption;
            }
            return selectedOption;
          });
          
          updatedAnswers[newQuestion.id] = mappedOptions.join(',');
          
        } else if (currentQuestion.type === 'radio' && 
            currentQuestion.options && 
            newQuestion.options &&
            currentQuestion.options.length > 0) {
              
          const optionIndex = currentQuestion.options.findIndex(opt => opt === answer);
          if (optionIndex !== -1 && optionIndex < newQuestion.options.length) {
            // Map to the same option index in the new language
            console.log(`Mapping radio option: "${answer}" at index ${optionIndex} to "${newQuestion.options[optionIndex]}"`);
            updatedAnswers[newQuestion.id] = newQuestion.options[optionIndex];
          } else {
            // If we can't find the option by index, just keep the original answer
            console.log(`Could not map radio option: "${answer}" - keeping original`);
            updatedAnswers[newQuestion.id] = answer;
          }
        } else {
          // For text and number questions, just copy the answer
          updatedAnswers[newQuestion.id] = answer;
        }
      }
      
      console.log('Mapped answers for new language:', updatedAnswers);
      return updatedAnswers;
    } catch (err) {
      console.error('Error mapping answers between languages:', err);
      return answers;
    }
  };

  const handleLanguageChange = (newLanguage: SupportedLanguage) => {
    console.log('Language changed to:', newLanguage);
    console.log('Current answers before mapping:', answers);
    
    const updatedAnswers = mapAnswersToNewLanguage(newLanguage);
    setAnswers(updatedAnswers);
    console.log('Mapped answers after language change:', updatedAnswers);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    console.log('Starting form submission...');
    console.log('Survey questions structure:', survey.questions);
    console.log('Current language:', language);
    console.log('Current answers:', answers);

    try {
      // Validate survey questions structure
      if (!survey.questions || typeof survey.questions !== 'object') {
        throw new Error('Invalid survey questions structure');
      }

      const questionsForLanguage = survey.questions[language];
      if (!questionsForLanguage || !Array.isArray(questionsForLanguage)) {
        throw new Error(`No questions found for language: ${language}`);
      }

      const questions = questionsForLanguage as Array<{
        id: string;
        type: string;
        question: string;
        required?: boolean;
        options?: string[];
      }>;

      console.log('Questions for validation:', questions);

      // Check for missing required fields
      const missingRequired = questions.filter(
        (q) => q.required && (!answers[q.id] || answers[q.id].trim() === '')
      );

      if (missingRequired.length > 0) {
        console.error('Missing required fields:', missingRequired.map(q => q.id));
        throw new Error(`Please fill in all required fields: ${missingRequired.map(q => q.question).join(', ')}`);
      }

      setIsSubmitting(true);
      
      // Calculate response time in seconds
      const responseTimeSeconds = Math.floor((Date.now() - startTime) / 1000);
      
      // Add the participant group to answers for analysis
      const finalAnswers = { ...answers };
      if (participantGroup) {
        finalAnswers['_participantGroup'] = participantGroup;
      }
      
      console.log('Submitting with final answers:', finalAnswers);
      console.log('Response time:', responseTimeSeconds, 'seconds');
      
      console.log('=== FORM SUBMISSION DEBUG ===');
      console.log('currentStep:', currentStep);
      console.log('Form state consent values (already initialized from previousConsent):');
      console.log('- hasStudyConsent:', hasStudyConsent);
      console.log('- hasDataConsent:', hasDataConsent);
      console.log('- phoneNumber:', phoneNumber);
      
      // State values are already initialized from previousConsent in useEffect
      // So we can use them directly without checking previousConsent again
      await onSubmit(
        finalAnswers, 
        language, 
        phoneNumber || undefined, 
        responseTimeSeconds,
        hasStudyConsent,
        hasDataConsent
      );
      setIsSubmitting(false);
    } catch (error) {
      console.error('Form submission error:', error);
      setIsSubmitting(false);
      throw error; // Re-throw to let the parent component handle it
    }
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    console.log('Answer changed:', questionId, value);
    
    // Store the answer value
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    
    // For radio options, try to also store the index position
    const questions = (survey.questions?.[language] || []) as Array<{
      id: string;
      type: string;
      options?: string[];
    }>;
    
    const question = questions.find(q => q.id === questionId);
    if (question && question.type === 'radio' && question.options) {
      const optionIndex = question.options.findIndex(opt => opt === value);
      if (optionIndex !== -1) {
        console.log(`Storing index ${optionIndex} for option "${value}" in question ${questionId}`);
        setAnswerIndices(prev => ({
          ...prev,
          [questionId]: optionIndex
        }));
      }
    }
  };

  const handleConsentContinue = (phone: string, studyConsent: boolean, dataConsent: boolean) => {
    setPhoneNumber(phone);
    setHasStudyConsent(studyConsent);
    setHasDataConsent(dataConsent);
    setShowQuestions(true);
    setStartTime(Date.now()); // Reset start time when questions appear
  };

  return {
    answers,
    isSubmitting,
    showQuestions,
    phoneNumber,
    hasStudyConsent,
    hasDataConsent,
    handleAnswerChange,
    handleConsentContinue,
    handleSubmit,
    handleLanguageChange
  };
};
